import { useApoioDoMes, useGerarApoioDoMes, useSituacaoDaIaDoMes } from '../../../api/queries/apoioDaIa'
import { useSessao } from '../../../auth/contexto'
import { podeFiscalizar } from '../../../auth/permissoes'
import { Botao } from '../../../components/Botao'
import { TEXTOS } from '../../../lib/glossario'
import type { EstadoDoApoio } from '../../../types/chamado'
import type {
  CoberturaDaAnalise as Cobertura,
  SuspeitasDeFracionamento as Suspeitas,
} from '../../../types/chamado'
import { AlertasDaCompetencia } from './AlertasDaCompetencia'
import { CoberturaDaAnalise } from './CoberturaDaAnalise'
import { ParecerDoMes } from './ParecerDoMes'
import { SuspeitasDeFracionamento } from './SuspeitasDeFracionamento'

/**
 * A conferência do mês, na ordem em que ela precisa ser lida.
 *
 * **Cobertura → parecer → alertas consolidados → suspeitas**, e a ordem não é arranjo de layout. A
 * cobertura é o denominador de tudo que vem depois: "2 chamados com Pendente usado para parar o
 * relógio" sobre 3 lidos de 105 é uma amostra de três, e sem a linha de cima lê-se como retrato do
 * mês. O parecer vem em seguida porque é o único texto que enquadra os números; os alertas e as
 * suspeitas são contagens, e ficam depois de quem as explica.
 *
 * **Nada aqui é vermelho, nada aqui bloqueia, e nada aqui pede que se espere.** Assinar o mês não
 * depende deste texto: os indicadores acima são do sistema. O único dano que esta área pode causar
 * sozinha é fazer o fiscal hesitar diante de um número correto, ou adiar o fechamento esperando um
 * parecer que talvez nunca chegue — por isso cada estado tem uma frase que diz, em pt-BR, que não é
 * erro e que não impede nada, e o botão continua ativo em todos eles menos enquanto a leitura está
 * de fato em voo.
 *
 * **Sem IA configurada, a área do parecer não aparece.** Aviso de recurso ausente numa tela de
 * fechamento é ruído: quem nunca teve a leitura não precisa saber que ela existe para assinar.
 * A cobertura, os alertas e as suspeitas continuam — nenhum deles depende de modelo nenhum.
 *
 * @param props a competência e as três contagens que o fechamento congelou
 * @returns a área de conferência do mês
 */
export function ConferenciaDoMes({
  competencia,
  cobertura,
  alertas,
  suspeitas,
}: {
  competencia: string
  cobertura: Cobertura
  alertas: { codigo: string; chamados: number }[]
  suspeitas: Suspeitas
}) {
  const { usuario } = useSessao()
  const situacao = useSituacaoDaIaDoMes(competencia)
  const apoio = useApoioDoMes(competencia)
  const gerar = useGerarApoioDoMes(competencia)

  // `!isError` derrubava a SEÇÃO INTEIRA quando a consulta de situação falhava — e junto dela o
  // parecer que já estava escrito, guardado no banco e carregado por outra consulta. Uma queda de
  // rede de dois segundos apagava da tela um texto que está em disco, e quem o tinha lido no dia
  // anterior conclui que ele foi perdido. A situação diz se DÁ para pedir uma leitura nova; ela não
  // é dona do que já existe. Por isso: havendo parecer, a seção fica de pé de qualquer jeito.
  const configurada = situacao.data?.disponivel ?? false
  const temParecer = Boolean(apoio.data?.parecer)
  const disponivel = configurada || temParecer

  return (
    <>
      <CoberturaDaAnalise cobertura={cobertura} />

      {disponivel && (
        <section className="cartao p-5" aria-label={TEXTOS.conferenciaDoMes.titulo}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-slate-900">{TEXTOS.conferenciaDoMes.titulo}</h2>
              <p className="mt-1 max-w-prose text-sm text-slate-500">{TEXTOS.conferenciaDoMes.ajuda}</p>
            </div>
            {/* Pedir a leitura é ato do fiscal (`@PreAuthorize(FISCAL)`) e custa tokens; e só se
                oferece quando o servidor confirmou que a IA está de pé. Com a situação em falha e
                um parecer guardado, o texto continua na tela e o botão não — não se convida
                ninguém a gastar uma chamada que não se sabe se existe. */}
            {configurada && podeFiscalizar(usuario) && (apoio.data?.podePedir ?? true) && (
              <Botao onClick={() => gerar.mutate()} disabled={gerar.isPending}>
                {apoio.data?.parecer ? TEXTOS.conferenciaDoMes.refazer : TEXTOS.conferenciaDoMes.botao}
              </Botao>
            )}
          </div>

          <div className="mt-4 space-y-3">
            <FraseDoEstado estado={apoio.data?.estado} motivo={apoio.data?.motivo ?? null} />
            {apoio.data?.parecer && (
              <ParecerDoMes
                competencia={competencia}
                parecer={apoio.data.parecer}
                podeDiscordar={podeFiscalizar(usuario)}
              />
            )}
          </div>
        </section>
      )}

      <AlertasDaCompetencia competencia={competencia} alertas={alertas} />
      <SuspeitasDeFracionamento suspeitas={suspeitas} />
    </>
  )
}

/**
 * A frase que explica em que pé está a leitura.
 *
 * **`PRONTO` não renderiza nada, de propósito:** o parecer logo abaixo fala por si, e uma frase
 * dizendo "está pronto" acima de um texto pronto é ruído. Todos os outros ganham uma frase, porque
 * "não veio parecer" escondia cinco situações com respostas diferentes — sendo preparada, no teto do
 * mês, serviço fora, falha recente, ou ninguém pediu.
 *
 * **O motivo do servidor tem precedência sobre o texto genérico.** Ele carrega o número do teto, o
 * que falta configurar, ou quanto tempo falta da quarentena — informação que a tela não tem.
 *
 * **Nenhuma variante usa `role="alert"` nem vermelho.** Nenhum destes estados é um erro do
 * fechamento, e quem vê vermelho ao lado de um número hesita em assiná-lo.
 *
 * @param props o estado e o motivo em pt-BR vindos do servidor
 * @returns a frase, ou nada quando o parecer fala por si
 */
function FraseDoEstado({ estado, motivo }: { estado: EstadoDoApoio | undefined; motivo: string | null }) {
  if (!estado || estado === 'PRONTO') {
    // Enquanto a consulta carrega também não se diz nada: a tela não espera por ela.
    return motivo ? <p className="text-sm text-slate-600">{motivo}</p> : null
  }
  const t = TEXTOS.conferenciaDoMes
  const generico: Record<Exclude<EstadoDoApoio, 'PRONTO'>, string> = {
    DESATUALIZADO: t.desatualizado,
    PREPARANDO: t.preparando,
    NAO_PEDIDO: t.naoPedido,
    NO_TETO: t.noTeto,
    SEM_CREDITO: t.semCredito,
    INDISPONIVEL: t.indisponivel,
    FALHOU: t.falhou,
  }
  // DESATUALIZADO ganha destaque âmbar: ele é o único estado em que há um texto completo na tela
  // dizendo coisas sobre um mês, e o texto está defasado. Sem a tarja, ele parece atual — que é
  // exatamente o risco que o portão de 24 horas cria.
  const destaque = estado === 'DESATUALIZADO' ? 'rounded-md bg-amber-50 p-3 text-amber-900' : 'text-slate-600'

  return (
    <div className={`text-sm ${destaque}`}>
      <p>{generico[estado]}</p>
      {motivo && motivo !== generico[estado] && <p className="mt-1">{motivo}</p>}
    </div>
  )
}
